import { Alert } from "types/alert";
import { Config, CompanySubscription } from "types/config";
import { Geofence } from "types/geofence";
import { FieldsBible } from "types/holy-books";
import { Rule } from "types/rule";
import { SystemStatus } from "types/system-status";
import { EcoSog } from "types/eco-sog";
import { WeatherData } from "types/weather";
import { DashboardMode, GuidanceMode } from "types/enums";

// --- Rules ---

const thresholdRule: Rule = {
  id: "1",
  created_at: "2024-03-13T12:00:00Z",
  modified_at: "2024-03-13T12:00:00Z",
  created_by: "ABC123",
  company_id: "ABC123",
  severity: "warning",
  type: "threshold",
  definition: {
    logic: {
      and: [
        {
          ">": [
            {
              var: "sog",
              type: "instant",
            },
            30,
          ],
        },
      ],
    },
    geofences: {
      enabled: true,
      ids: ["1"],
      area_operation: "inside",
    },
  },
  vessels: {
    apply_to_all: true,
    ids: [],
  },
  notifications: {
    push_notifications: {
      enabled: false,
      apply_to_all: false,
      user_ids: [],
    },
    email: {
      enabled: true,
      user_ids: ["123"],
    },
    sms: {
      enabled: true,
      user_ids: ["123"],
    },
    helm: {
      enabled: true,
      show_native_alert_banner: true,
      requires_acknowledgement: true,
    },
    console: {
      enabled: true,
    },
  },
  enabled: true,
};

const thresholdRule2: Rule = {
  id: "1",
  created_at: "2024-03-13T12:00:00Z",
  modified_at: "2024-03-13T12:00:00Z",
  created_by: "ABC123",
  company_id: "ABC123",
  severity: "critical",
  type: "threshold",
  message: "High impact, please slow down!",
  definition: {
    logic: {
      and: [
        {
          ">": [
            {
              var: "impact",
              type: "instant",
            },
            4,
          ],
        },
        {
          ">": [
            {
              var: "engine_speed",
              type: "range",
              period_sec: 10,
            },
            500,
          ],
        },
      ],
    },
    geofences: {
      enabled: true,
      ids: ["1"],
      area_operation: "inside",
    },
  },
  vessels: {
    apply_to_all: true,
    ids: [],
  },
  notifications: {
    push_notifications: {
      enabled: false,
      apply_to_all: false,
      user_ids: [],
    },
    email: {
      enabled: true,
      user_ids: ["123"],
    },
    sms: {
      enabled: true,
      user_ids: ["123"],
    },
    helm: {
      enabled: true,
      show_native_alert_banner: true,
      requires_acknowledgement: true,
    },
    console: {
      enabled: true,
    },
  },
  enabled: true,
};

const geofenceRule: Rule = {
  id: "2",
  created_at: "2024-03-13T12:00:00Z",
  modified_at: "2024-03-13T12:00:00Z",
  created_by: "ABC123",
  company_id: "ABC123",
  severity: "info",
  type: "geofence",
  definition: {
    trigger_on: "cross",
    geofences: {
      ids: ["1"],
    },
  },
  vessels: {
    apply_to_all: true,
    ids: [],
  },
  notifications: {
    push_notifications: {
      enabled: false,
      apply_to_all: false,
      user_ids: [],
    },
    email: {
      enabled: true,
      user_ids: ["123"],
    },
    sms: {
      enabled: true,
      user_ids: ["123"],
    },
    helm: {
      enabled: true,
      show_native_alert_banner: true,
      requires_acknowledgement: true,
    },
    console: {
      enabled: true,
    },
  },
  enabled: true,
};

// --- Alerts ---

export const thresholdAlert: Alert = {
  id: "123456",
  company_id: "ABC123",
  vessel_id: "123",
  trigger_time: "1710329446",
  type: "threshold",
  coordinates: {
    lat: 64.17226681661569,
    lon: -21.900767148071434,
  },
  info: {
    fields: [{ name: "sog" }],
    rule: thresholdRule,
  },
};

export const thresholdAlert2: Alert = {
  id: "123456",
  vessel_id: "123",
  company_id: "ABC123",
  trigger_time: "1710329446",
  type: "threshold",
  coordinates: {
    lat: 0,
    lon: 0,
  },
  info: {
    fields: [
      { name: "impact", device: { name: "Sensor 2" } },
      { name: "engine_speed" },
    ],
    rule: thresholdRule2,
  },
};

export const geofenceAlert: Alert = {
  id: "123456",
  vessel_id: "123",
  company_id: "ABC123",
  trigger_time: "1710329446",
  type: "geofence",
  coordinates: {
    lat: 0,
    lon: 0,
  },
  info: {
    geofence_ids: ["1"],
    rule: geofenceRule,
  },
};

export const externalAlert: Alert = {
  id: "123456",
  vessel_id: "123",
  company_id: "ABC123",
  trigger_time: "1710329446",
  type: "external",
  coordinates: {
    lat: 0,
    lon: 0,
  },
  info: {
    message: "Engine cold start warning",
    device: {
      name: "Engine",
      instance: 0,
      message_source: 1,
    },
  },
};

// Variants of the alerts above with a chosen severity and without the
// acknowledgement requirement, so DevTools can trigger every toast style
// (the rules above all set requires_acknowledgement)
const withSeverity = (alert: Alert, severity: Rule["severity"]): Alert => {
  const rule = alert.info.rule;
  if (!rule) return alert;
  return {
    ...alert,
    info: {
      ...alert.info,
      rule: {
        ...rule,
        severity,
        notifications: {
          ...rule.notifications,
          helm: {
            ...rule.notifications.helm,
            requires_acknowledgement: false,
          },
        },
      },
    },
  };
};

export const criticalAlert = withSeverity(thresholdAlert2, "critical");
export const warningAlert = withSeverity(thresholdAlert, "warning");
export const infoAlert = withSeverity(geofenceAlert, "info");

export const alerts = [
  thresholdAlert,
  geofenceAlert,
  thresholdAlert2,
  externalAlert,
];

// --- Geofences ---

const circularGeofence: Geofence = {
  id: "1",
  company_id: "ABC123",
  created_at: "2024-03-13T12:00:00Z",
  name: "Open Ocean",
  radius: 5000,
  type: "circle",
  coordinates: [{ lat: 0, lon: 0 }],
};

const polygonalGeofence: Geofence = {
  id: "2",
  company_id: "DEF456",
  created_at: "2024-03-13T12:00:00Z",
  name: "Marine Protected Area",
  type: "polygon",
  coordinates: [
    { lat: 10, lon: -30 },
    { lat: 10, lon: -20 },
    { lat: 0, lon: -20 },
    { lat: 0, lon: -30 },
    { lat: 10, lon: -30 },
  ],
};

const linearGeofence: Geofence = {
  id: "3",
  company_id: "GHI789",
  created_at: "2024-03-13T12:00:00Z",
  name: "Underwater Pipeline",
  type: "line",
  coordinates: [
    { lat: 20, lon: -150 },
    { lat: 15, lon: -145 },
  ],
};

const geofences: Geofence[] = [
  circularGeofence,
  polygonalGeofence,
  linearGeofence,
];

// --- Config / Settings / Status / Other tools ----

export const mockLocalStorage = {
  key1: "key1 data",
  key2: true,
  key3: {
    subkey1: "subkey1 data",
    subkey2: [1, 2, 3],
  },
  key4: [1, 2, 3, "4"],
};

const mockFieldsBible: FieldsBible = {
  sog: {
    label: "SOG",
    unit: "kn",
    description: "The speed of the vessel relative to the ground.",
    rule: { enabled: true },
  },
  impact: {
    label: "Impact",
    unit: "g",
    description: "The force of impact on the vessel.",
    rule: { enabled: true },
  },
};

export const mockSystemStatus: SystemStatus = {
  sensors: [
    { name: "Sensor Unit 1", connected: true },
    { name: "Sensor Unit 2", connected: false },
  ],
  nmea: {
    connected: true,
    gps: false,
    engine_data: false,
  },
  network: {
    internet: true,
    modem: true,
    eth0: {
      connected: true,
      internet: true,
    },
    eth1: {
      connected: false,
      internet: true,
    },
  },
  hardware: {
    computer_temperature: 33,
    hdmi: true,
    data_usage: 44,
    storage_usage: 55,
  },
};

export const mockCompanySubscription: CompanySubscription = {
  features_transformed: {
    "safety-speed": "{}",
    "eco-speed": "{}",
    "sea-sickness": "{}",
  },
  company_id: "ABC123",
  package_code: "monitor",
  status: "active",
  created_at: "2024-01-01T00:00:00Z",
  updated_at: "2024-01-01T00:00:00Z",
  package: {
    code: "monitor",
    title: "Monitor",
    description: "Basic monitoring package",
    sort_order: 1,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    features: [],
  },
  overrides: [],
};

export const mockConfig: Config = {
  vessel: "Test vessel",
  has_fog: true,
  has_fuel_range_estimation: true,
  has_usb_transfer: true,
  has_threshold_overwrite: true,
  max_impact: 3,
  max_speed: 30,
  target_fuel_rate: 2.5,
  boat_length: 11,
  device_id: "1234567890",
  vessel_id: "123",
  guidance_mode: GuidanceMode.Combined,
  dashboard_mode: DashboardMode.Gauges,
  captains: [
    { id: "1", name: "Smith", rank: 5 },
    { id: "2", name: "Johnson", rank: 3 },
    { id: "3", name: "Davis", rank: 7 },
    { id: "6", name: "Miller", rank: 4 },
    { id: "7", name: "Brown", rank: 6 },
    { id: "8", name: "Taylor", rank: 9 },
    { id: "9", name: "Martinez", rank: 1 },
    { id: "10", name: "Garcia", rank: 5 },
    { id: "11", name: "Alexander Jonathan Lee", rank: 6 },
    { id: "12", name: "Stephanie Rose White", rank: 3 },
    { id: "13", name: "Jonathan Harris III", rank: 8 },
    { id: "14", name: "Benjamin Clark", rank: 2 },
    { id: "15", name: "Kimberly Turner", rank: 7 },
    { id: "16", name: "Theodore Roosevelt Evans", rank: 4 },
    { id: "17", name: "Isabella Rodriguez", rank: 1 },
    { id: "18", name: "William Allen Jr.", rank: 5 },
    { id: "19", name: "Samantha Carter", rank: 9 },
    { id: "20", name: "Christopher Moore With A Really Long Name", rank: 3 },
  ],
  company_settings: {
    has_comfort_speed: false,
    force_captain_login: false,
  },
  holy_books: {
    fields_bible: mockFieldsBible,
  },
  geofences: geofences,
  company_subscription: mockCompanySubscription,
};

export const mockWeatherData: WeatherData = {
  time: "1748937600000000",
  timestamp: "1748940038154148",
  air_temperature: 3.80999994277954,
  cloud_cover: 60.7999992370605,
  current_direction: 5.59000015258789,
  current_speed: 0.219999998807907,
  gust: 19.8199996948242,
  humidity: 59.2299995422363,
  ice_cover: 0,
  precipitation: 0.0399999991059303,
  pressure: 993.590026855469,
  sea_level: -0.759999990463257,
  sea_state: 7,
  sea_state_wave: 4,
  swell_direction: 227,
  swell_height: 0.259999990463257,
  swell_period: 8.94999980926514,
  visibility: 24.1299991607666,
  water_temperature: 9.72000026702881,
  wave_direction: 331.739990234375,
  wave_height: 2,
  wave_period: 3.99000000953674,
  wind_direction: 352.489990234375,
  wind_speed: 15.539999961853,
  wind_wave_direction: 350.839996337891,
  wind_wave_height: 1.29999995231628,
  wind_wave_period: 4.98000001907349,
};

// Weather older than an hour is hidden as stale (see useWeather), so
// simulated weather must carry a current forecast time
export const freshWeatherData = (
  base: WeatherData = mockWeatherData
): WeatherData => {
  const nowMicroseconds = (Date.now() * 1000).toString();
  return { ...base, time: nowMicroseconds, timestamp: nowMicroseconds };
};

export const mockEcoSog: EcoSog = {
  eco_speed: 27,
  eco_rpm: 2500,
  fuel_rate_warn_threshold: mockConfig?.target_fuel_rate
    ? mockConfig.target_fuel_rate + 0.2
    : null,
  max_sog: 0,
  max_rpm: 3800,
  fuel_deviation_from_pred: {
    trip: -2.35,
  },
  avg_sog: {
    trip: 15.49,
    sailing: 15.49,
    "10s": 15.49,
    "30s": 15.49,
    "120s": 15.49,
  },
  avg_fuel_rate_l_h: {
    trip: 42.0,
    sailing: 42.0,
    "10s": 42.0,
    "30s": 42.0,
    "120s": 42.0,
  },
  avg_fuel_rate_l_nm: {
    trip: 2.71,
    sailing: 2.71,
    "10s": 2.71,
    "30s": 2.71,
    "120s": 2.71,
  },
  eco_ranges: [
    {
      start: 4.0,
      end: 5.0,
      peak: 4.5,
      best: 4.5,
      start_rpm: 800,
      end_rpm: 1000,
      peak_rpm: 900,
      best_rpm: 900,
    },
    {
      start: 25.05,
      end: 33.5,
      peak: 26.63,
      best: 33.5,
      start_rpm: 2000,
      end_rpm: 2500,
      peak_rpm: 2200,
      best_rpm: 2500,
    },
    {
      start: 55.05,
      end: 72.5,
      peak: 26.63,
      best: 33.5,
      start_rpm: 2000,
      end_rpm: 2500,
      peak_rpm: 2200,
      best_rpm: 2500,
    },
  ],
  fuel_curve_model_coef: {
    scaler: { mean: [22.4], std: [11.256738908505934] },
    poly: { degrees: 19 },
    linear: {
      coef: [
        0.0, -0.2748986844966833, -0.6511841425348843, -2.861819864261105,
        11.707708004062262, 12.675016577308362, -46.23077376242896,
        -21.0953776588521, 84.27222446276177, 15.408055582331212,
        -83.42208487280756, -3.1886852554601703, 47.73582464435469,
        -2.1805317914065734, -15.810997945794067, 1.5394100961494535,
        2.817802107396873, -0.36815689086656195, -0.20921892611813342,
        0.03198907199333048,
      ],
      intercept: 1.9717581880454977,
    },
  },
  fuel_curve_model_corrected_coef: {
    scaler: { mean: [22.4], std: [11.256738908505934] },
    poly: { degrees: 19 },
    linear: {
      coef: [
        0.0, -0.2748986844966833, -0.6511841425348843, -2.861819864261105,
        11.707708004062262, 12.675016577308362, -46.23077376242896,
        -21.0953776588521, 84.27222446276177, 15.408055582331212,
        -83.42208487280756, -3.1886852554601703, 47.73582464435469,
        -2.1805317914065734, -15.810997945794067, 1.5394100961494535,
        2.817802107396873, -0.36815689086656195, -0.20921892611813342,
        0.03198907199333048,
      ],
      intercept: 1.9717581880454977,
    },
  },
  fuel_curve_stats: {
    state: "ok",
    max_fuel_rate_per_hour: 96.47636741773567,
    min_fuel_rate_per_hour: 5.856232332136048,
    max_fuel_rate_per_nm: 2.8712423828624787,
    min_fuel_rate_per_nm: 1.6118015258649785,
    stable_fuel_curve_sog_range: [3.8, 41.8],
    outlier_free_range: null,
  },
  fuel_ghost: {
    upper: [],
    lower: [],
  },
};
